import React, { useState, useCallback, useMemo } from 'react'
import { Modal, Radio, Checkbox, Button } from 'antd'
import dayjs from 'dayjs'
import vipIcon from '../../../../../images/icon_vip_personal.png'

export interface DownloadConfig {
  folderRule: 'none' | 'date' | 'address' | 'watermark'
  namingFields: string[]
}

interface Props {
  open: boolean
  count: number
  onCancel: () => void
  onConfirm: (config: DownloadConfig) => void
  confirmLoading?: boolean
}

const FOLDER_OPTIONS: Array<{
  label: string
  value: DownloadConfig['folderRule']
}> = [
  { label: '不归类', value: 'none' },
  { label: '按拍摄日期', value: 'date' },
  { label: '按拍摄地点', value: 'address' },
  { label: '按拍摄水印', value: 'watermark' }
]

const NAMING_OPTIONS = [
  { label: '照片编号', value: 'photoNumber' },
  { label: '拍摄日期', value: 'time' },
  { label: '拍摄地点', value: 'address' },
  { label: '水印名称', value: 'watermark' }
]

const DownloadModal: React.FC<Props> = ({
  open,
  count,
  onCancel,
  onConfirm,
  confirmLoading
}) => {
  const [folderRule, setFolderRule] =
    useState<DownloadConfig['folderRule']>('none')
  const [namingFields, setNamingFields] = useState<string[]>(['photoNumber'])

  const previewName = useMemo(() => {
    const parts: string[] = []
    if (namingFields.includes('time'))
      parts.push(dayjs().format('YYYYMMDD_HHmmss'))
    if (namingFields.includes('address')) parts.push('北京市朝阳区')
    if (namingFields.includes('watermark')) parts.push('外勤打卡')
    if (namingFields.includes('photoNumber')) parts.push('001')
    return (parts.join('_') || 'photo') + '.jpg'
  }, [namingFields])

  const handleConfirm = useCallback(() => {
    onConfirm({ folderRule, namingFields })
  }, [folderRule, namingFields, onConfirm])

  const disabled = namingFields.length === 0 || confirmLoading

  return (
    <Modal
      open={open}
      title={`图片/视频下载——已选择 ${count} 张照片`}
      onCancel={onCancel}
      footer={null}
      width={520}
      destroyOnHidden
    >
      <div style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 12 }}>
          <span style={{ fontSize: 16, fontWeight: 500, color: '#000' }}>
            照片归类方式
          </span>
          <span
            style={{ fontSize: 14, color: 'rgba(0,0,0,0.45)', marginLeft: 8 }}
          >
            对照片进行归类，整理成文件夹
          </span>
        </div>
        <Radio.Group
          value={folderRule}
          onChange={(e) => setFolderRule(e.target.value)}
        >
          {FOLDER_OPTIONS.map((o) => (
            <Radio.Button key={o.value} value={o.value}>
              {o.label}
            </Radio.Button>
          ))}
        </Radio.Group>
      </div>

      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <span style={{ fontSize: 16, fontWeight: 500, color: '#000' }}>
            照片视频命名方式
          </span>
          <span
            style={{ fontSize: 14, color: 'rgba(0,0,0,0.45)', marginLeft: 8 }}
          >
            对下载的照片重命名
          </span>
        </div>
        <Checkbox.Group
          value={namingFields}
          onChange={(v) => setNamingFields(v as string[])}
        >
          {NAMING_OPTIONS.map((o) => (
            <Checkbox key={o.value} value={o.value} style={{ marginRight: 24 }}>
              {o.label}
            </Checkbox>
          ))}
        </Checkbox.Group>
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          background: '#F5F5F5',
          borderRadius: 8,
          height: 64,
          padding: '0 12px',
          marginBottom: 24
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            borderRadius: 4,
            background: '#D9D9D9',
            flexShrink: 0,
            marginRight: 12
          }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', marginBottom: 4 }}
          >
            命名预览
          </div>
          <div
            style={{
              fontSize: 14,
              color: 'rgba(0,0,0,0.85)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}
          >
            {previewName}
          </div>
        </div>
      </div>

      <Button
        type="primary"
        block
        disabled={!!disabled}
        loading={confirmLoading}
        onClick={handleConfirm}
        style={{
          height: 44,
          borderRadius: 8,
          fontSize: 16,
          fontWeight: 600,
          background: disabled ? undefined : '#0070F4',
          borderColor: disabled ? undefined : '#0070F4',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8
        }}
      >
        <img src={vipIcon} alt="" style={{ width: 20, height: 20 }} />
        会员免费下载
      </Button>
    </Modal>
  )
}

export default DownloadModal
